import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Icon, SimpleGrid, useColorModeValue } from '@chakra-ui/react';
import {
  collection,
  getFirestore,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { MdAccessTime, MdBarChart, MdCalendarToday } from 'react-icons/md';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import {
  buildAssignedInstructorClassesQuery,
  filterInstructorVisibleClasses,
} from 'utils/instructorClassQueries';

const db = getFirestore(app);

export default function InstructorStats(props) {
  const { ...rest } = props;
  const { userDocId, userData } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');

  useEffect(() => {
    if (!userDocId) {
      setClasses([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const assignedQuery = buildAssignedInstructorClassesQuery({
      db,
      collection,
      query,
      where,
      instructorId: userDocId,
    });

    const unsubscribe = onSnapshot(
      assignedQuery,
      (snapshot) => {
        const nextClasses = snapshot.docs.map((classDoc) => ({
          id: classDoc.id,
          ...classDoc.data(),
        }));
        setClasses(filterInstructorVisibleClasses(nextClasses));
        setLoading(false);
      },
      (error) => {
        console.error('Error loading instructor dashboard stats:', error);
        setLoading(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [userDocId]);

  const classesThisMonth = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
    return classes.filter((classData) => classData.Date >= monthStart && classData.Date <= monthEnd).length;
  }, [classes]);

  const memberSince = useMemo(() => {
    const rawDate = userData?.AccountCreationDate || userData?.CreatedAt || userData?.createdAt;
    if (!rawDate) return 'N/A';
    const date = typeof rawDate === 'number' ? new Date(rawDate) : rawDate.toDate?.() || new Date(rawDate);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'America/Los_Angeles',
    });
  }, [userData]);

  return (
    <SimpleGrid columns={{ base: 1, md: 3, xl: 3 }} gap="20px" mb="20px" {...rest}>
      <MiniStatistics
        startContent={(
          <IconBox
            w="56px"
            h="56px"
            bg={boxBg}
            icon={<Icon w="32px" h="32px" as={MdCalendarToday} color={brandColor} />}
          />
        )}
        name="Classes This Month"
        value={loading ? '...' : classesThisMonth.toString()}
      />
      <MiniStatistics
        startContent={(
          <IconBox
            w="56px"
            h="56px"
            bg={boxBg}
            icon={<Icon w="32px" h="32px" as={MdBarChart} color={brandColor} />}
          />
        )}
        name="Total Classes"
        value={loading ? '...' : classes.length.toString()}
      />
      <MiniStatistics
        startContent={(
          <IconBox
            w="56px"
            h="56px"
            bg={boxBg}
            icon={<Icon w="32px" h="32px" as={MdAccessTime} color={brandColor} />}
          />
        )}
        name="Member Since"
        value={loading ? '...' : memberSince}
      />
    </SimpleGrid>
  );
}
